import { noop } from 'lodash';
import { lighten } from 'polished';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useExpanded, useFlexLayout, useSortBy, useTable } from 'react-table';
import styled from 'styled-components';

import { BREAKPOINTS, COLORS } from 'config/constants';
import { generateSelectorsFromTestIds } from 'helpers/utilities';

import { InfoCircleIcon } from 'components/Icons';
import Caret from 'components/Icons/Caret';
import { CONTAINER_PADDING } from 'components/Layout/Container/Container';
import TextHeader from 'components/TextHeader';
import Tooltip from 'components/Tooltip';

const TableWrapper = styled.div`
  // NOTE: on small screens we try to match the Container offset, otherwise the
  // 1.5rem offset lines the table content up with the container edge on desktop
  left: -1.5rem;
  position: relative;
  width: calc(100% + 3rem);
  @media screen and (max-width: ${BREAKPOINTS.SM}px) {
    left: -${CONTAINER_PADDING.SMALL}rem;
    width: calc(100% + ${CONTAINER_PADDING.SMALL * 2}rem);
  }
`;

const StyledTable = styled.table`
  color: #999;
  position: relative;
  th {
    padding-bottom: 0.4rem;
    padding-top: 0.4rem;
  }
  td {
    align-items: center;
    display: flex;
    padding-bottom: 0.6rem;
    padding-top: 0.6rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  th,
  td {
    border-bottom: 1px solid ${COLORS.GRAY_LIGHTEST};
    overflow: hidden;
    padding-left: 0.8rem;
    padding-right: 0.8rem;
    text-overflow: ellipsis;
    &:first-child {
      padding-left: 0;
    }
    &:last-child {
      padding-right: 0;
    }
  }
  tbody {
    display: flex;
    flex-direction: column;
    tr {
      position: relative;
      // :nth-child(even) {
      //   background-color: #fafafa;
      // }
      &:hover {
        background: #fafafa;
        color: #666;
        .show-on-hover {
          opacity: 1;
        }
        .show-on-hover.disabled {
          opacity: 0.7;
        }
      }
      &:last-child td {
        border-bottom: none;
      }
    }
    ${({ $pagination, $hasData }) =>
      $pagination &&
      $hasData &&
      `
      border-bottom: 1px solid #f0f0f0;
      padding-bottom: 0.5rem;
    `}
  }
  tr {
    width: 100%;
  }
  a {
    color: inherit;
    &:hover {
      color: ${COLORS.PRIMARY};
      text-decoration: none;
    }
  }
`;

const TableRow = styled.tr`
  border-radius: 1rem;

  ${({ $isActive }) =>
    $isActive &&
    `
    background: ${COLORS.GRAY_SUPER_LIGHT};
    border-radius: 0;
    td {
      border-bottom: 1px solid transparent;
    }
  `}

  cursor: ${({ onClick }) => onClick && 'pointer'};
  padding: 0 1.5rem;
  position: relative;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.6;
    pointer-events: none;
    cursor: not-allowed;
  `};
`;

const TableHeadRow = styled.tr`
  padding: 0 1.5rem;
`;

const StyledHeaderCell = styled.th`
  ${({ $center }) =>
    $center &&
    `
    align-items: center;
    display: flex;
    text-align: center;
    justify-content: center;
  `};

  ${({ $sortable }) =>
    $sortable &&
    `
    &:hover div {
      color: ${COLORS.GRAY_DARK};
    }
  `}
`;

const StyledTextHeader = styled(TextHeader)`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  text-align: left;
  width: 100%;

  ${({ $center }) =>
    $center &&
    `
    justify-content: center;
    text-align: center;
  `};
`;

const ExpandableCell = styled.td`
  padding-left: 1.5rem !important;
  padding-right: 1.5rem !important;
  background: ${COLORS.GRAY_SUPER_LIGHT};
`;

const StyledTableCell = styled.td`
  ${({ $center }) => $center && 'justify-content: center;'}
`;

/**
 * [Table]
 *
 * To see the various table properties, review the following docs:
 * https://react-table.tanstack.com/docs/api/useTable
 *
 * @param {func}     [checkIfActiveRow] - function that receives 'row' properies. If true, active styling applied to row
 * @param {object[]} columns - columns array as defined by 'react-table', but handles the following custom properties:
 * @param {bool}     columns.$.[hide] - if true, column will be hidden entirely. Useful for hiding columns on mobile
 * @param {bool}     columns.$.[canSort]
 * @param {string}   columns.$.[headerTestId] - optionally pass a test id for the table header cell
 * @param {object[]} data - data to be used for rendering table rows.
 * @param {number} [expandedIndex] - if using expanded table rows, this indicates which row index to render the expanded
 * content below
 * @param {bool} [hideHeader] - if true, will hide the table header entirely
 * @param {bool} [autoResetSortBy=true] if false, updates to data will not reset to default sort to initialSortBy state
 * @param {object[]} [initialSortBy] - Table sort will be handled by react table. Don't pass if paginated table.
 *  Example: [{ id: sortId, desc: sortDir === 'desc' }]
 * @param {object[]} [sortBy] if passed, the table sort will be manually controlled by this array.
 *  Example: [{ id: sortId, desc: sortDir === 'desc' }]
 * @param {function} [setSortBy] handles updating sortBy.  Receives column as the argument
 * @param {bool} [isLoading] - will hide the sort icons until is loading is finished
 * @param {func} [renderExpandableContent] - render prop for rendering the expanded row content
 * @param {string || func} [rowDataTestId] - if function, receives row properites
 *
 * @returns {object}
 */

// Create a default prop getter
const defaultPropGetter = () => ({});

const getTableHeaderProps = (column, setSortBy) => {
  if (column.getSortByToggleProps) {
    return column.getSortByToggleProps();
  }

  if (setSortBy && column.canSort) {
    // NOTE: alternatively, we could use the `manualSortBy` option provided by react table. More here:
    // https://github.com/TanStack/react-table/discussions/2033
    return {
      style: { cursor: 'pointer' },
      onClick: () => setSortBy(column)
    };
  }

  return {};
};

const Table = ({
  checkIfActiveRow,
  columns,
  data,
  'data-testid': testId,
  enableExpanding = true,
  autoResetSortBy,
  setSortBy, // pass custom sort handler
  sortBy, // pass if using controlled sortBy state (must include setSortByQuery)
  style,
  initialSortBy, // pass if using react table's default sorting (must not be server paginated)
  expandedIndex = null,
  getRowProps = defaultPropGetter,
  isLoading,
  hideHeader,
  onRowClick,
  onRowDoubleClick = noop,
  renderExpandableContent,
  rowDataTestId,
  pagination
}) => {
  const useManualSort = !!setSortBy && !!sortBy;
  const useDefaultSorting = !!initialSortBy && !useManualSort;

  const plugins = [useFlexLayout];
  if (useDefaultSorting) plugins.push(useSortBy);
  if (enableExpanding) plugins.push(useExpanded);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns
  } = useTable(
    {
      columns,
      data,
      autoResetSortBy,
      manualSortBy: useManualSort,
      disableSortBy: useManualSort && pagination && isLoading,
      disableMultiSort: true,
      initialState: {
        expanded: { [expandedIndex]: true },
        sortBy: initialSortBy
      }
    },
    ...plugins
  );

  const renderRowSubComponent = useCallback(
    ({ row }) => renderExpandableContent(row),
    [renderExpandableContent]
  );

  const columnsFlat = columns
    .map(column => (column.columns ? column.columns : column))
    .flat();

  return (
    <TableWrapper data-testid={testId} style={style}>
      <StyledTable
        {...getTableProps()}
        style={{ width: '100%' }}
        $pagination={pagination}
        $hasData={data.length > 0}
      >
        {!hideHeader && (
          <thead>
            {headerGroups.map((headerGroup, index) => {
              const { key, role, style, className } = headerGroup.getHeaderGroupProps();
              return (
                <TableHeadRow
                  key={key || `TableHeadRow-${index}`}
                  role={role}
                  style={style}
                  className={className}
                >
                  {headerGroup.headers.map(column => {
                    if (column.hide) return null;

                    const { key, ...headerCellProps } = column.getHeaderProps(
                      getTableHeaderProps(column, setSortBy)
                    );

                    return (
                      // eslint-disable-next-line react/jsx-key
                      <StyledHeaderCell
                        {...headerCellProps}
                        key={key}
                        $sortable={column.canSort}
                        data-testid={column.headerTestId}
                        $center={column.center}
                        title="" // was display "Toggle SortBy" as title attribute by default (if sortable)
                      >
                        <StyledTextHeader
                          type={TextHeader.TYPES.XSMALL}
                          weight={TextHeader.WEIGHTS.NORMAL}
                          className="mb-0"
                          $center={column.center}
                        >
                          {column.render('Header')}

                          {column.headerToolTip && (
                            <Tooltip
                              content={column.headerToolTip}
                              placement="top"
                              delayHide={200}
                              triggerContainerElement="div"
                              {...(column.headerTooltipProps || {})}
                            >
                              <InfoCircleIcon
                                color="currentColor"
                                title=""
                                size={12}
                                $sortable={column.canSort}
                                style={{ flexShrink: 0 }}
                              />
                            </Tooltip>
                          )}

                          <TableSortIcon column={column} sortBy={sortBy} />
                        </StyledTextHeader>
                      </StyledHeaderCell>
                    );
                  })}
                </TableHeadRow>
              );
            })}
          </thead>
        )}

        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            const isRowActive = checkIfActiveRow ? checkIfActiveRow(row) : false;
            const rowTestId =
              typeof rowDataTestId === 'function' ? rowDataTestId(row) : rowDataTestId;
            const rowData = row.original;
            const { key, ...rowProps } = row.getRowProps(getRowProps(row));

            return (
              <React.Fragment key={key}>
                <TableRow
                  $isActive={isRowActive}
                  onDoubleClick={e => onRowDoubleClick({ event: e, rowData, rowIndex })}
                  onClick={
                    onRowClick
                      ? e => onRowClick({ event: e, rowData, rowIndex })
                      : undefined
                  }
                  data-testid={rowTestId}
                  {...rowProps}
                >
                  {row.cells.map((cell, cellIndex) => {
                    const column = columnsFlat[cellIndex];
                    if (!column || column.hide) return null;

                    return (
                      <StyledTableCell
                        {...cell.getCellProps()}
                        $center={column.center}
                        key={`${rowIndex}:${cellIndex}`}
                      >
                        {cell.render('Cell')}
                      </StyledTableCell>
                    );
                  })}
                </TableRow>

                {renderExpandableContent && isRowActive ? (
                  <tr key={`${key}-${rowIndex}-expandable`}>
                    <ExpandableCell
                      colSpan={visibleColumns.length}
                      data-testid={Table.TEST_IDS.EXPANDABLE_CONTENT}
                    >
                      {renderRowSubComponent({ row })}
                    </ExpandableCell>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

const TableSortIcon = ({ column, isLoading, sortBy }) => {
  if (!column.canSort || isLoading) return null;

  const sortedColumn = sortBy && sortBy.find(s => s.id === column.id);
  const isSorted = sortBy ? !!sortedColumn : column.isSorted;
  const isSortedDesc = sortBy ? sortedColumn?.desc : column.isSortedDesc;

  if (!isSorted) {
    return <Table.SortDefaultIcon />;
  }

  return isSortedDesc ? <Table.SortDescendIcon /> : <Table.SortAscendIcon />;
};

TableSortIcon.propTypes = {
  column: PropTypes.shape(),
  isLoading: PropTypes.bool,
  sortBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool
    })
  )
};

Table.SortAscendIcon = () => (
  <Caret
    direction={Caret.DIRECTIONS.UP}
    size={11}
    color={lighten(0.12, COLORS.PRIMARY)}
    data-testid={Table.TEST_IDS.SORT_ASC_ICON}
  />
);

Table.SortDescendIcon = () => (
  <Caret
    direction={Caret.DIRECTIONS.DOWN}
    size={11}
    color={lighten(0.12, COLORS.PRIMARY)}
    data-testid={Table.TEST_IDS.SORT_DESC_ICON}
  />
);

Table.SortDefaultIcon = () => (
  <Caret
    direction={Caret.DIRECTIONS.DOWN}
    size={11}
    color={COLORS.GRAY_LIGHT}
    data-testid={Table.TEST_IDS.IS_SORTABLE_ICON}
  />
);

Table.TEST_IDS = {
  EXPANDABLE_CONTENT: 'Table-ExpandableContent',
  IS_SORTABLE_ICON: 'Table-is-sortable',
  SORT_DESC_ICON: 'Table-sort-desc',
  SORT_ASC_ICON: 'Table-sort-asc'
};

Table.SELECTORS = generateSelectorsFromTestIds(Table.TEST_IDS);

Table.propTypes = {
  autoResetSortBy: PropTypes.bool,
  checkIfActiveRow: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.shape()),
  data: PropTypes.arrayOf(PropTypes.shape()),
  'data-testid': PropTypes.string,
  enableExpanding: PropTypes.bool,
  expandedIndex: PropTypes.number,
  getRowProps: PropTypes.any,
  hideHeader: PropTypes.bool,
  initialSortBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool
    })
  ),
  isLoading: PropTypes.bool,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  pagination: PropTypes.bool,
  renderExpandableContent: PropTypes.func,
  rowDataTestId: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  setSortBy: PropTypes.func,
  sortBy: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      desc: PropTypes.bool
    })
  ),
  style: PropTypes.shape()
};

export default Table;
